import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const PAGE_SIZE = 50;

export function useFeedPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // True when the latest load failed. A failed fetch must never look like an
  // empty feed: it used to return [], which wiped the posts on a pull-to-refresh
  // with poor signal and told the member "No posts yet".
  const [error, setError] = useState(false);
  // Only the newest load may write its result, so a slow response can't land on
  // top of a newer one.
  const latestLoad = useRef(0);

  // null means the fetch failed; an empty array means there really is nothing.
  const fetchPage = useCallback(async (offset) => {
    try {
      const { data, error: rpcError } = await supabase.rpc('get_feed_posts', {
        p_limit: PAGE_SIZE,
        p_offset: offset,
      });

      if (rpcError) {
        console.error('useFeedPosts error:', rpcError);
        return null;
      }

      return data ?? [];
    } catch (err) {
      console.error('useFeedPosts error:', err);
      return null;
    }
  }, []);

  // Fetch page one and drop any pages beyond it. Resolves false if the fetch
  // failed, leaving whatever is on screen in place.
  const loadFirstPage = useCallback(async () => {
    const load = ++latestLoad.current;
    const data = await fetchPage(0);
    if (load !== latestLoad.current) return true;

    if (data === null) {
      setError(true);
      return false;
    }
    setPosts(data);
    setHasMore(data.length === PAGE_SIZE);
    setError(false);
    return true;
  }, [fetchPage]);

  useEffect(() => {
    let cancelled = false;

    loadFirstPage().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
      latestLoad.current += 1;
    };
  }, [loadFirstPage]);

  // Pull-to-refresh and app-resume land here; `loading` deliberately stays false
  // so the feed never blanks out under the user mid-refresh.
  const refresh = loadFirstPage;

  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    const load = latestLoad.current;
    const data = await fetchPage(posts.length);
    if (data !== null && load === latestLoad.current) {
      setPosts(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    }
    setLoadingMore(false);
  }, [fetchPage, posts.length, loadingMore, hasMore]);

  return { posts, loading, loadingMore, hasMore, error, fetchMore, refresh };
}
